/**
 * Platform Admin — Provider 管理 (Provider Catalog P0 +
 * VISTOERA_PROVIDER_REGISTRATION_BATCH_FUNCTION_NODE_BINDING_AND_NODE_
 * LEVEL_PROVIDER_OPTION_P0_V1; superseded in relevant part by
 * VISTOERA_CANONICAL_PROVIDER_MODEL_CATALOG_NODE_MODEL_BINDING_USAGE_
 * BILLING_FOUNDATION_P0_V1 — task spec §8/§32/§33).
 *
 * Provider Definition metadata only — never secrets. Credentials are
 * created separately on the Credential Center page.
 *
 * Canonical model (this round): a Global Provider Definition (e.g.
 * DeepSeek) is created here ONCE. Function Node availability is no
 * longer decided at Provider-creation time — Provider ≠ Model (task
 * spec principle A), and a Provider can expose many ProviderModels
 * across many modalities. Node availability is now decided per
 * ProviderModel via 功能节点管理 → 添加模型 (NodeModelBinding), after
 * 模型管理 syncs/enables the Provider's specific models. The old
 * "可用功能节点（Legacy）" edit action below is kept read/write-
 * compatible ONLY for already-bound legacy rows (task spec §8/§28 — no
 * Big Bang migration, no deletion of legacy data); it is intentionally
 * removed from the Add-Provider flow.
 */
import { EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { Button, Checkbox, Form, Input, Modal, Table, Tag, message } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  platformBindProviderFunctionNodes,
  platformCreateOpenAICompatibleProvider,
  platformListCompatibleFunctionNodes,
  platformListCredentialProviders,
  platformUnbindProviderFunctionNode,
  platformUpdateProviderDefinition,
} from '../api'
import type { CompatibleFunctionNodeRow, ProviderDefinitionRow } from '../types'
import { SectionCard, formatDateTime } from './adminShared'
import { platformColors } from './theme'
import { blankAddProviderFormValues } from './providerRegistryAddForm'

function errMsg(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { code?: string; detail?: { code?: string } } } })?.response?.data
  const code = data?.code || data?.detail?.code
  const raw = (err as { message?: string })?.message
  return code ? `${raw || fallback} (${code})` : raw || fallback
}

/**
 * Task spec §6/§7 — dynamic Function Node checklist, never a hardcoded
 * capability-key list. Fetches GET .../providers/compatible-function-nodes
 * for the given adapter_type (also flags already-bound state when
 * editing an existing Provider).
 */
function FunctionNodeSelector(props: {
  adapterType: string
  providerId?: string
  value: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
}) {
  const { adapterType, providerId, value, onChange, disabled } = props
  const [nodes, setNodes] = useState<CompatibleFunctionNodeRow[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    platformListCompatibleFunctionNodes(adapterType, providerId)
      .then((res) => setNodes(res.data.nodes ?? []))
      .catch((err) => message.error(errMsg(err, '加载功能节点列表失败')))
      .finally(() => setLoading(false))
  }, [adapterType, providerId])

  if (loading) {
    return <div style={{ color: platformColors.mutedText, fontSize: 13 }}>加载功能节点中…</div>
  }
  if (nodes.length === 0) {
    return <div style={{ color: platformColors.mutedText, fontSize: 13 }}>当前 Adapter 暂无兼容的功能节点</div>
  }

  return (
    <Checkbox.Group
      value={value}
      onChange={(checked) => onChange(checked as string[])}
      disabled={disabled}
      style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      {nodes.map((node) => (
        <Checkbox key={node.capability} value={node.capability}>
          {node.display_name_zh}
          <span style={{ color: platformColors.mutedText, fontSize: 12, marginLeft: 6 }}>({node.capability})</span>
          {node.already_bound && node.is_default ? (
            <Tag color="gold" style={{ marginLeft: 6 }}>
              该节点默认
            </Tag>
          ) : null}
        </Checkbox>
      ))}
    </Checkbox.Group>
  )
}

export default function ProviderRegistryPage() {
  const [rows, setRows] = useState<ProviderDefinitionRow[]>([])
  const [loading, setLoading] = useState(true)
  const [addOpen, setAddOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm()

  const [editRow, setEditRow] = useState<ProviderDefinitionRow | null>(null)
  const [editSelectedNodes, setEditSelectedNodes] = useState<string[]>([])
  const [editInitialNodes, setEditInitialNodes] = useState<string[]>([])
  const [editSaving, setEditSaving] = useState(false)
  const [editNodesVersion, setEditNodesVersion] = useState(0)

  const load = useCallback(() => {
    setLoading(true)
    platformListCredentialProviders()
      .then((res) => setRows(res.data.providers ?? []))
      .catch((err) => message.error(err?.message || '加载 Provider 列表失败'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const openAdd = () => {
    form.resetFields()
    form.setFieldsValue(blankAddProviderFormValues())
    setAddOpen(true)
  }

  const submitAdd = async () => {
    const values = await form.validateFields()
    setSaving(true)
    try {
      // VISTOERA_CANONICAL_PROVIDER_MODEL_CATALOG_NODE_MODEL_BINDING_
      // USAGE_BILLING_FOUNDATION_P0_V1 (task spec §32/§33) — Provider
      // creation no longer atomically binds Function Nodes. Node
      // availability is now a ProviderModel/NodeModelBinding decision,
      // made in 功能节点管理 → 添加模型 (after 模型管理 → 同步/启用
      // models for this Provider), never a manual model_id/region entry
      // at creation time.
      await platformCreateOpenAICompatibleProvider({
        provider_key: String(values.provider_key).trim().toLowerCase(),
        display_name: String(values.display_name).trim(),
        base_url: String(values.base_url).trim(),
      })
      message.success('Provider 已创建 — 请到凭证中心添加 API Key，再到模型管理同步/启用模型')
      setAddOpen(false)
      load()
    } catch (err) {
      message.error(errMsg(err, '创建 Provider 失败'))
    } finally {
      setSaving(false)
    }
  }

  const toggleStatus = async (row: ProviderDefinitionRow) => {
    const next = row.status === 'active' ? 'disabled' : 'active'
    try {
      await platformUpdateProviderDefinition(row.id, { status: next })
      message.success(next === 'active' ? '已启用' : '已停用')
      load()
    } catch (err) {
      message.error(errMsg(err, '更新状态失败'))
    }
  }

  const openEdit = (row: ProviderDefinitionRow) => {
    setEditRow(row)
    setEditSelectedNodes([])
    setEditInitialNodes([])
    setEditNodesVersion((v) => v + 1)
    platformListCompatibleFunctionNodes(row.adapter_type || 'OPENAI_COMPATIBLE', row.id)
      .then((res) => {
        const bound = (res.data.nodes ?? []).filter((n) => n.already_bound).map((n) => n.capability)
        setEditSelectedNodes(bound)
        setEditInitialNodes(bound)
      })
      .catch((err) => message.error(errMsg(err, '加载已绑定功能节点失败')))
  }

  const submitEdit = async () => {
    if (!editRow) return
    const toAdd = editSelectedNodes.filter((c) => !editInitialNodes.includes(c))
    const toRemove = editInitialNodes.filter((c) => !editSelectedNodes.includes(c))
    if (toAdd.length === 0 && toRemove.length === 0) {
      setEditRow(null)
      return
    }
    setEditSaving(true)
    try {
      if (toAdd.length > 0) {
        await platformBindProviderFunctionNodes(editRow.id, toAdd)
      }
      for (const capability of toRemove) {
        // Sequential, not Promise.all — a guarded 409
        // (CANNOT_UNBIND_DEFAULT_OPTION) on one capability must not
        // abort/hide the outcome of the others (task spec §19).
        try {
          await platformUnbindProviderFunctionNode(editRow.id, capability)
        } catch (err) {
          message.error(errMsg(err, `移除功能节点绑定失败（${capability}）`))
        }
      }
      message.success('已更新可用功能节点')
      setEditRow(null)
      load()
    } catch (err) {
      message.error(errMsg(err, '更新功能节点绑定失败'))
    } finally {
      setEditSaving(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: platformColors.primaryText }}>Provider 管理</h1>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: platformColors.secondaryText, maxWidth: 720 }}>
            Provider Definition 描述调用方式与能力，不保存 Secret。创建 Provider 后，在
            <Link to="/platform/provider-credentials">凭证中心</Link>
            为其添加 API Key（支持共享 Preview + Production），到
            <Link to="/platform/models">模型管理</Link>
            同步/启用具体模型，最后在
            <Link to="/platform/providers">功能节点管理</Link>
            「添加模型」中批量绑定到需要的功能节点。
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
            添加 Provider
          </Button>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            刷新
          </Button>
        </div>
      </div>

      <SectionCard title="Provider Catalog">
        <Table
          rowKey="id"
          loading={loading}
          dataSource={rows}
          pagination={false}
          size="middle"
          scroll={{ x: 1200 }}
          columns={[
            {
              title: 'Provider',
              dataIndex: 'display_name',
              render: (name: string, row) => (
                <div>
                  <div style={{ fontWeight: 600 }}>{name}</div>
                  <div style={{ fontSize: 11, color: platformColors.mutedText }}>{row.provider_key}</div>
                </div>
              ),
            },
            {
              title: 'Adapter',
              dataIndex: 'adapter_type',
              width: 150,
              render: (v: string | undefined, row) => (
                <div>
                  <Tag>{v || 'NATIVE_API'}</Tag>
                  {row.source === 'ADMIN_DEFINED' && <Tag color="blue">Admin</Tag>}
                </div>
              ),
            },
            {
              title: 'Base URL',
              dataIndex: 'base_url',
              width: 200,
              ellipsis: true,
              render: (v: string | null) => v || <span style={{ color: platformColors.mutedText }}>—</span>,
            },
            {
              title: '兼容能力',
              dataIndex: 'supported_capabilities',
              render: (caps: string[]) =>
                caps?.length ? (
                  caps.map((c) => <Tag key={c}>{c}</Tag>)
                ) : (
                  <span style={{ color: platformColors.mutedText }}>—</span>
                ),
            },
            {
              title: '状态',
              dataIndex: 'status',
              width: 90,
              render: (s: string) => (
                <Tag color={s === 'active' ? 'success' : 'default'}>{s === 'active' ? '启用' : '停用'}</Tag>
              ),
            },
            {
              title: '凭证',
              dataIndex: 'credential_count',
              width: 80,
              render: (n: number, row) =>
                row.credential_required ? (
                  <Link to={`/platform/provider-credentials?provider_key=${encodeURIComponent(row.provider_key)}`}>{n}</Link>
                ) : (
                  <span style={{ color: platformColors.mutedText }}>—</span>
                ),
            },
            {
              title: 'Models',
              dataIndex: 'registered_model_count',
              width: 80,
              render: (n: number | null | undefined) =>
                typeof n === 'number' ? n : <span style={{ color: platformColors.mutedText }}>—</span>,
            },
            {
              title: '绑定',
              dataIndex: 'bound_capability_count',
              width: 70,
              render: (n: number | undefined) =>
                typeof n === 'number' ? n : <span style={{ color: platformColors.mutedText }}>—</span>,
            },
            {
              title: '就绪状态',
              dataIndex: 'readiness_status',
              width: 130,
              render: (v: ProviderDefinitionRow['readiness_status']) => {
                if (v === 'READY') return <Tag color="success">Ready</Tag>
                if (v === 'CREDENTIAL_REQUIRED') return <Tag color="warning">需要凭据</Tag>
                if (v === 'MODEL_OR_BINDING_REQUIRED') return <Tag color="orange">需要模型/绑定</Tag>
                return <span style={{ color: platformColors.mutedText }}>—</span>
              },
            },
            {
              title: '更新时间',
              dataIndex: 'updated_at',
              width: 150,
              render: (v: string | null) => formatDateTime(v),
            },
            {
              title: '操作',
              key: 'actions',
              width: 180,
              fixed: 'right',
              render: (_: unknown, row) => (
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)}>
                    可用功能节点（Legacy）
                  </Button>
                  <Button size="small" onClick={() => toggleStatus(row)}>
                    {row.status === 'active' ? '停用' : '启用'}
                  </Button>
                </div>
              ),
            },
          ]}
        />
      </SectionCard>

      <Modal
        title="添加 Provider（OpenAI Compatible）"
        open={addOpen}
        onCancel={() => {
          form.resetFields()
          setAddOpen(false)
        }}
        onOk={() => void submitAdd()}
        confirmLoading={saving}
        okText="创建"
        cancelText="取消"
        destroyOnClose
        afterClose={() => form.resetFields()}
      >
        <p style={{ fontSize: 13, color: platformColors.secondaryText, marginBottom: 8 }}>
          此表单仅用于注册 OpenAI Compatible Provider（名称、Base URL）。Native Provider（例如 Volcengine Ark / 火山方舟）已在下方
          Catalog 预置 — 请在
          <Link to="/platform/provider-credentials">凭证中心</Link>
          为其绑定 API Key。
        </p>
        <p style={{ fontSize: 13, color: platformColors.secondaryText, marginBottom: 16 }}>
          API Key 不会在此页面填写；同一 Provider 可绑定多个 Credential 账号（按 priority 路由）。
        </p>
        <Form form={form} layout="vertical" requiredMark="optional" initialValues={blankAddProviderFormValues()}>
          <Form.Item name="display_name" label="名称" rules={[{ required: true, message: '请输入显示名称' }]}>
            <Input placeholder="显示名称" />
          </Form.Item>
          <Form.Item
            name="provider_key"
            label="Provider ID"
            rules={[
              { required: true, message: '请输入 Provider ID' },
              { pattern: /^[a-z0-9_-]+$/, message: '仅小写字母、数字、下划线、连字符' },
            ]}
            extra="创建后不可修改（已有凭证时）。"
          >
            <Input placeholder="provider_id" />
          </Form.Item>
          <Form.Item name="base_url" label="Base URL" rules={[{ required: true, message: '请输入 Base URL' }]}>
            <Input placeholder="https://api.example.com" />
          </Form.Item>
          <p style={{ fontSize: 12, color: platformColors.mutedText, marginTop: -4 }}>
            创建后请到<Link to="/platform/provider-credentials">凭证中心</Link>添加 API Key，再到
            <Link to="/platform/models">模型管理</Link>同步/启用模型，最后在
            <Link to="/platform/providers">功能节点管理</Link>「添加模型」中批量绑定到具体节点。
          </p>
        </Form>
      </Modal>

      <Modal
        title={editRow ? `可用功能节点（Legacy） — ${editRow.display_name}` : '可用功能节点（Legacy）'}
        open={!!editRow}
        onCancel={() => setEditRow(null)}
        onOk={() => void submitEdit()}
        confirmLoading={editSaving}
        okText="保存"
        cancelText="取消"
        destroyOnClose
      >
        <p style={{ fontSize: 12, color: platformColors.mutedText, marginBottom: 12 }}>
          <Tag color="default" style={{ marginRight: 6 }}>
            Legacy
          </Tag>
          此入口保留用于兼容既有绑定的读取/移除，新绑定请优先使用
          <Link to="/platform/providers">功能节点管理 → 添加模型</Link>
          （基于 ProviderModel / NodeModelBinding）。勾选此 Provider 应该在哪些功能节点上可用；取消勾选会从对应功能节点移除该 Provider
          的绑定（不会删除全局 Provider 或凭证）；如果该绑定是某功能节点当前的默认 Provider，需要先在功能节点管理中改选其他默认
          Provider 才能移除。
        </p>
        {editRow && (
          <FunctionNodeSelector
            key={editNodesVersion}
            adapterType={editRow.adapter_type || 'OPENAI_COMPATIBLE'}
            providerId={editRow.id}
            value={editSelectedNodes}
            onChange={setEditSelectedNodes}
            disabled={editSaving}
          />
        )}
      </Modal>
    </div>
  )
}
